/**
 * SlotLegend Component
 *
 * Displays a legend for different slot types.
 *
 * Props:
 * - types: Array of objects [{ key: string, label: string }]
 *   - key   → used as React key and CSS class
 *   - label → text displayed for the legend
 *
 * Example:
 * <SlotLegend types={[{ key: "available", label: "Available" }]} />
 */

import { Button } from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import moment from 'moment-timezone';
import AppPopover from '../../../../components/popover/popover';
dayjs.extend(duration);

export default function SlotLegend({ types }) {
    return (
        <div className="slot-legend">
            {types.map(({ key, label, description }) => (
                <AppPopover
                    key={key}
                    content={<div style={{ maxWidth: 200, fontSize: 10 }}>{description}</div>}
                    trigger="hover" // popover shows on hover
                >
                    <span className={`legend ${key}`}>{label}</span>
                </AppPopover>
            ))}
        </div>
    );
}

// Re usbale Action Button
export const ActionButton = ({ type = 'primary', icon, label, disabled = false, onClick, className = 'small-btn', imgStyle = 'btn-icon' }) => {
    return (
        <Button type={type} disabled={disabled} onClick={onClick} className={className}>
            {label}
            {icon && <img src={icon} alt={label || 'icon'} className={imgStyle} />}
        </Button>
    );
};

/**
 * SectionTitle Component
 *
 * Renders a reusable title block with an optional icon (image) and label.
 **/
export const SectionTitle = ({ icon, label, className = '', style = {}, imgStyle = {} }) => {
    return (
        <div className={className} style={style}>
            {/* Render icon only if provided */}
            {icon && <img src={icon} alt={label || 'icon'} style={imgStyle} />}

            {/* Render label text */}
            {label}
        </div>
    );
};

/**
 * Converts a slot duration string (e.g., "2h 30m", "1h", "45m") into total minutes.
 *
 * @function parseSlotExp
 * @param {string} value - A string representing a duration in hours and/or minutes.
 *                        Examples: "2h 30m", "1h", "45m".
 * @returns {number} - The total duration in minutes. Returns 0 if input is falsy.
 *
 * @example
 * parseSlotExp("2h 30m"); // 150
 * parseSlotExp("1h");     // 60
 * parseSlotExp("45m");    // 45
 * parseSlotExp("");       // 0
 */
export function parseSlotExp(value) {
    // If the input is empty, null, or undefined → return 0
    if (!value) return 0;

    // Start with totalMinutes = 0
    let totalMinutes = 0;

    // Use regex to find hours (e.g., "2h", "10h")
    const hourMatch = value.match(/(\d+)\s*h/);

    // Use regex to find minutes (e.g., "30m", "5m")
    const minuteMatch = value.match(/(\d+)\s*m/);

    // If hours are present, convert to minutes and add to total
    if (hourMatch) {
        totalMinutes += parseInt(hourMatch[1], 10) * 60;
    }

    // If minutes are present, add them directly
    if (minuteMatch) {
        totalMinutes += parseInt(minuteMatch[1], 10);
    }

    // Return the total duration in minutes
    return totalMinutes;
}

const parseHour = (timeStr) => {
    // Split into time part and AM/PM modifier
    let [time, modifier] = timeStr.split(' ');

    // Extract the hour (ignore minutes for grouping)
    let [hours] = time.split(':').map(Number);

    // If PM and not 12 → add 12 (e.g. 1 PM → 13, 11 PM → 23)
    if (modifier?.toLowerCase() === 'pm' && hours !== 12) hours += 12;

    // If AM and 12 → midnight → set to 0
    if (modifier?.toLowerCase() === 'am' && hours === 12) hours = 0;

    // Return normalized 24-hour value
    return hours;
};

/**
 * Groups time slots into morning, afternoon, and evening buckets.
 **/
export const groupSlots = (slots) => {
    const groups = { morning: [], afternoon: [], evening: [] };

    slots.forEach((s) => {
        const hour = parseHour(s.as_starttime);
        if (hour >= 5 && hour < 12) groups.morning.push(s);
        else if (hour >= 12 && hour < 17) groups.afternoon.push(s);
        else if (hour >= 17 && hour <= 23) groups.evening.push(s);
    });
    return groups;
};

/**
 * Calculate the remaining time until an expiry timestamp.
 *
 * @function getTimeRemaining
 * @param {string|Date} expiry - The expiry timestamp (ISO string, Date, or moment-parsable string).
 * @returns {string} A human-readable string representing the remaining time:
 *  - `'-'` → if no expiry or invalid input
 *  - `'Expired'` → if the expiry time has already passed
 *  - e.g. `"2h 15m"` → if more than 1 hour remains
 *  - e.g. `"10m"` → if less than 1 hour remains
 *
 * @example
 * getTimeRemaining("2025-09-03T12:30:00Z"); // "2h 10m"
 * getTimeRemaining("2025-09-03T09:05:00Z"); // "Expired"
 * getTimeRemaining(null);                   // "-"
 */
export const getTimeRemaining = (expiry) => {
    if (!expiry) return '-'; // Guard: no expiry given

    // 🔑 Treat expiry as LOCAL by removing "Z" (which would force UTC)
    const expiryTime = moment(expiry.replace('Z', ''));
    const currentTime = moment();

    // Invalid timestamp check
    if (!expiryTime.isValid()) return '-';

    // Expired already
    if (expiryTime.isBefore(currentTime)) return 'Expired';

    // Calculate difference
    const duration = moment.duration(expiryTime.diff(currentTime));

    // Break into hours + minutes
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    // Format result
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};
